import {useEffect, useState} from "react";
import {useAuth} from "../context/AuthContext";
import {getRestaurantsByOwnerId, updateRestaurantSettings} from "../api/api";
import {getBookingsByRestaurantId} from "../api/api";
import RestaurantStatsCard from "../components/restaurants/RestaurantStatsCard";
import BookingsTable from "../components/restaurants/BookingsTable";
import AddRestaurantForm from "../components/restaurants/AddRestaurantForm";
import EditRestaurantForm from "../components/restaurants/EditRestaurantForm";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "../components/ui/tabs";
import {Card, CardContent, CardHeader, CardTitle} from "../components/ui/card";
import {Button} from "../components/ui/button";
import {Skeleton} from "../components/ui/skeleton";
import {Dialog, DialogContent, DialogTrigger} from "../components/ui/dialog";
import {Helmet} from "react-helmet";
import {PlusCircle, LayoutDashboard, BookOpen, Settings, FileEdit, Calendar, Star} from "lucide-react";
import {toast} from "../hooks/use-toast";
import CalendarTab from "../components/restaurants/CalendarTab";
import KarmaScore from "../components/restaurants/KarmaScore";


const RestaurantDashboard = () => {
    const [updatingVisibility, setUpdatingVisibility] = useState(false);
    const [updatingAutoAccept, setUpdatingAutoAccept] = useState(false);
    const [updatingEmailNotifications, setUpdatingEmailNotifications] = useState(false);
    const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(null);
    const [isAddRestaurantDialogOpen, setIsAddRestaurantDialogOpen] = useState(false);
    const [isEditRestaurantDialogOpen, setIsEditRestaurantDialogOpen] = useState(false);

    const [restaurants, setRestaurants] = useState<any[]>([]);
    const [restaurantsError, setRestaurantsError] = useState<string | null>(null);
    const [isLoadingRestaurants, setIsLoadingRestaurants] = useState(false);

    const [bookings, setBookings] = useState<any[]>([]);
    const [bookingsError, setBookingsError] = useState<string | null>(null);
    const [isLoadingBookings, setIsLoadingBookings] = useState(false);

    const [isAutoAccept, setIsAutoAccept] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [isEmailEnabled, setIsEmailEnabled] = useState(false);

    const selectedRestaurant = restaurants.find((r) => r.id === selectedRestaurantId) || null;

    useEffect(() => {
        const fetchRestaurants = async () => {
            if (!isAuthenticated || !user?.id || !user?.is_restaurant_owner) return;
            try {
                setIsLoadingRestaurants(true);
                const data = await getRestaurantsByOwnerId(user.id);
                setRestaurants(data);
            } catch (err: any) {
                setRestaurantsError(err.message || "Failed to load restaurants");
            } finally {
                setIsLoadingRestaurants(false);
            }
        };
        fetchRestaurants();
    }, [isAuthenticated, user]);

    const fetchBookings = async () => {
        try {
            setIsLoadingBookings(true);
            const data = await getBookingsByRestaurantId(selectedRestaurantId!);
            setBookings(data);
        } catch (err: any) {
            setBookingsError(err.message || "Failed to load bookings");
        } finally {
            setIsLoadingBookings(false);
        }
    };

    useEffect(() => {
        if (!selectedRestaurantId) return;

        fetchBookings();
    }, [selectedRestaurantId]);

    useEffect(() => {
        if (restaurants.length > 0 && !selectedRestaurantId) {
            setSelectedRestaurantId(restaurants[0].id);
        }
    }, [restaurants]);

    useEffect(() => {
        setIsAutoAccept(!!selectedRestaurant?.is_auto_accept);
        setIsActive(!!selectedRestaurant?.is_active);
        setIsEmailEnabled(!!selectedRestaurant?.is_email_enabled);
    }, [selectedRestaurant]);

    if (isAuthLoading) {
        return (
            <div className="container mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
                <div className="space-y-6">
                    <Skeleton className="h-8 w-72"/>
                    <Skeleton className="h-48 w-full"/>
                    <Skeleton className="h-64 w-full"/>
                </div>
            </div>
        );
    }


    if (!isAuthenticated || !user) {
        return (
            <div className="container mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
                <div className="rounded-lg bg-white p-8 text-center shadow dark:bg-gray-800">
                    <h1 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                        Login Required
                    </h1>
                    <p className="mb-6 text-gray-600 dark:text-gray-300">
                        Please login to access the restaurant dashboard.
                    </p>
                    <Button asChild className="bg-primary text-white hover:bg-red-700">
                        <a href="/public">Return to Home</a>
                    </Button>
                </div>
            </div>
        );
    }

    if (!user.is_restaurant_owner) {
        return (
            <div className="container mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
                <div className="rounded-lg bg-white p-8 text-center shadow dark:bg-gray-800">
                    <h1 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                        Restaurant Owner Access Only
                    </h1>
                    <p className="mb-6 text-gray-600 dark:text-gray-300">
                        This dashboard is only available to restaurant owners. To become a
                        partner, please contact us.
                    </p>
                    <Button asChild className="bg-primary text-white hover:bg-red-700">
                        <a href="/public">Return to Home</a>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Restaurant Dashboard | SurpriseTable</title>
                <meta
                    name="description"
                    content="Manage your restaurant, view bookings, and track performance with the SurpriseTable restaurant dashboard."
                />
            </Helmet>

            <div className="container mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
                <div
                    className="mb-8 flex flex-col items-start justify-between space-y-4 md:flex-row md:items-center md:space-y-0">
                    <div>
                        <h1 className="font-serif text-3xl font-bold text-gray-800 dark:text-white">
                            Restaurant Dashboard
                        </h1>
                        <p className="mt-1 text-gray-600 dark:text-gray-300">
                            Manage your restaurants and view booking details
                        </p>
                    </div>
                    <Dialog
                        open={isAddRestaurantDialogOpen}
                        onOpenChange={setIsAddRestaurantDialogOpen}
                    >
                        <DialogTrigger asChild>
                            <Button className="bg-primary text-white hover:bg-red-700">
                                <PlusCircle className="mr-2 h-4 w-4"/>
                                Add Restaurant
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[600px]">
                            <AddRestaurantForm
                                userId={user.id}
                                onSuccess={() => setIsAddRestaurantDialogOpen(false)}
                            />
                        </DialogContent>
                    </Dialog>
                </div>

                {isLoadingRestaurants ? (
                    <div className="space-y-6">
                        <Skeleton className="h-12 w-full"/>
                        <div className="grid gap-6 md:grid-cols-3">
                            <Skeleton className="h-32 w-full"/>
                            <Skeleton className="h-32 w-full"/>
                            <Skeleton className="h-32 w-full"/>
                        </div>
                        <Skeleton className="h-96 w-full"/>
                    </div>
                ) : restaurantsError ? (
                    <Card className="p-6 text-center">
                        <CardContent>
                            <p className="text-red-600 dark:text-red-400">
                                Error loading restaurants. Please try again later.
                            </p>
                        </CardContent>
                    </Card>
                ) : !restaurants || restaurants.length === 0 ? (
                    <Card className="p-6 text-center">
                        <CardContent>
                            <h2 className="mb-4 text-xl font-bold text-gray-800 dark:text-white">
                                No Restaurants Found
                            </h2>
                            <p className="mb-6 text-gray-600 dark:text-gray-300">
                                You haven't added any restaurants yet.
                            </p>
                            <Button
                                onClick={() => setIsAddRestaurantDialogOpen(true)}
                                className="bg-primary text-white hover:bg-red-700"
                            >
                                <PlusCircle className="mr-2 h-4 w-4"/>
                                Add Your First Restaurant
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                            <ul className="flex flex-wrap -mb-px text-sm font-medium text-center overflow-x-auto">
                                {restaurants.map((restaurant) => (
                                    <li key={restaurant.id} className="mr-2">
                                        <button
                                            onClick={() => setSelectedRestaurantId(restaurant.id)}
                                            className={`inline-block p-4 border-b-2 rounded-t-lg ${
                                                selectedRestaurantId === restaurant.id
                                                    ? "text-primary dark:text-primary-dark border-primary dark:border-primary-dark"
                                                    : "border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                                            }`}
                                        >
                                            {restaurant.name}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {selectedRestaurant && (
                            <>
                                <div className="mb-6 flex items-center justify-between">
                                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                                        {selectedRestaurant.name}
                                    </h2>
                                    <Dialog
                                        open={isEditRestaurantDialogOpen}
                                        onOpenChange={setIsEditRestaurantDialogOpen}
                                    >
                                        <DialogTrigger asChild>
                                            <Button variant="outline">
                                                <FileEdit className="mr-2 h-4 w-4"/>
                                                Edit Restaurant
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="sm:max-w-[600px]">
                                            <EditRestaurantForm
                                                restaurant={selectedRestaurant}
                                                onSuccess={() => setIsEditRestaurantDialogOpen(false)}
                                            />
                                        </DialogContent>
                                    </Dialog>
                                </div>

                                <Tabs defaultValue="overview" className="space-y-6">
                                    <TabsList>
                                        <TabsTrigger value="overview" className="flex items-center">
                                            <LayoutDashboard className="mr-2 h-4 w-4"/>
                                            Overview
                                        </TabsTrigger>
                                        <TabsTrigger value="calendar" className="flex items-center">
                                            <Calendar className="mr-2 h-4 w-4" />
                                            Calendar
                                        </TabsTrigger>

                                        <TabsTrigger value="bookings" className="flex items-center">
                                            <BookOpen className="mr-2 h-4 w-4"/>
                                            Bookings
                                        </TabsTrigger>
                                        <TabsTrigger value="karma" className="flex items-center">
                                            <Star className="mr-2 h-4 w-4" />
                                            Buy Karma Score
                                        </TabsTrigger>
                                        <TabsTrigger value="settings" className="flex items-center">
                                            <Settings className="mr-2 h-4 w-4"/>
                                            Settings
                                        </TabsTrigger>
                                    </TabsList>

                                    <TabsContent value="overview" className="space-y-6">
                                        <div
                                            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                                            <RestaurantStatsCard
                                                title="Total Bookings"
                                                value={(bookings?.length || 0).toString()}
                                                icon="bookings"
                                                trend="up"
                                                trendValue="12%"
                                            />
                                            <RestaurantStatsCard
                                                title="Revenue"
                                                value={`€${(bookings?.reduce(
                                                    (sum, booking) => sum + booking.total_price,
                                                    0
                                                ) || 0).toFixed(2)}`}
                                                icon="revenue"
                                                trend="up"
                                                trendValue="8%"
                                            />
                                            <RestaurantStatsCard
                                                title="Times Chosen"
                                                value={selectedRestaurant.times_chosen ? selectedRestaurant.times_chosen.toString() : "0"}
                                                icon="timesChosen"
                                                trend="up"
                                                trendValue="8%"
                                            />
                                            <RestaurantStatsCard
                                                title="Karma Power"
                                                value={selectedRestaurant.karma_power ? selectedRestaurant.karma_power.toString() : "0"}
                                                icon="karmaPower"
                                                trend="up"
                                                trendValue="8%"
                                            />
                                            <RestaurantStatsCard
                                                title="Rating"
                                                value={selectedRestaurant.rating.toString()}
                                                icon="rating"
                                                trend="stable"
                                                trendValue=""
                                                suffix="/5"
                                            />
                                        </div>

                                        <Card>
                                            <CardHeader>
                                                <CardTitle>Restaurant Information</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                                    <div>
                                                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                            Cuisine
                                                        </h3>
                                                        <p className="mt-1 text-gray-800 dark:text-white">
                                                            {selectedRestaurant.cuisine}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                            Location
                                                        </h3>
                                                        <p className="mt-1 text-gray-800 dark:text-white">
                                                            {selectedRestaurant.location}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                            Price
                                                        </h3>
                                                        <p className="mt-1 text-gray-800 dark:text-white">
                                                            €{selectedRestaurant.price.toFixed(2)}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                            Rating
                                                        </h3>
                                                        <p className="mt-1 flex items-center text-gray-800 dark:text-white">
                                                            {selectedRestaurant.rating}
                                                            <span className="ml-1 text-yellow-400">
                                                              <svg
                                                                  xmlns="http://www.w3.org/2000/svg"
                                                                  className="h-4 w-4 fill-current"
                                                                  viewBox="0 0 20 20"
                                                              >
                                                                <path
                                                                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                                              </svg>
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="mt-6">
                                                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Description
                                                    </h3>
                                                    <p className="mt-1 text-gray-800 dark:text-white">
                                                        {selectedRestaurant.description}
                                                    </p>
                                                </div>

                                                <div className="mt-6">
                                                    <img
                                                        src={selectedRestaurant.image}
                                                        alt={selectedRestaurant.name}
                                                        className="h-64 w-full rounded-md object-cover"
                                                    />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </TabsContent>


                                    <TabsContent value="bookings">
                                        {isLoadingBookings ? (
                                            <Skeleton className="h-96 w-full"/>
                                        ) : bookingsError ? (
                                            <Card className="p-6 text-center">
                                                <CardContent>
                                                    <p className="text-red-600 dark:text-red-400">
                                                        Error loading bookings. Please try again later.
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        ) : !bookings || bookings.length === 0 ? (
                                            <Card className="p-6 text-center">
                                                <CardContent>
                                                    <h2 className="mb-4 text-xl font-bold text-gray-800 dark:text-white">
                                                        No Bookings Yet
                                                    </h2>
                                                    <p className="text-gray-600 dark:text-gray-300">
                                                        When customers book your restaurant, their bookings
                                                        will appear here.
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        ) : (
                                            <BookingsTable bookings={bookings} refreshBookings={fetchBookings} />
                                        )}
                                    </TabsContent>
                                    <TabsContent value="calendar" className="space-y-6">
                                        <CalendarTab bookings={bookings} />
                                    </TabsContent>
                                    <TabsContent value="karma" className="space-y-6">
                                        <KarmaScore
                                            karmaPower={selectedRestaurant.karma_power}
                                            adjustedKarmaPower={selectedRestaurant.adjusted_karma_power}
                                            timesChosen={selectedRestaurant.times_chosen} restaurantId={selectedRestaurant.id}                                        />
                                    </TabsContent>



                                    <TabsContent value="settings">
                                        <Card>
                                            <CardHeader>
                                                <CardTitle>Restaurant Settings</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-4">

                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <h3 className="text-lg font-medium text-gray-800 dark:text-white">
                                                                Visibility
                                                            </h3>
                                                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                                                When disabled, your restaurant won't be shown in the
                                                                app.
                                                            </p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            disabled={updatingVisibility}
                                                            onClick={async () => {
                                                                try {
                                                                    setUpdatingVisibility(true);
                                                                    const updated = await updateRestaurantSettings(selectedRestaurantId!, {
                                                                        acceptBookings: !isActive,
                                                                    });
                                                                    setIsActive(!!updated.is_active);
                                                                    toast({ title: "Updated", description: "Visibility updated." });
                                                                } catch (err: any) {
                                                                    toast({ title: "Update failed", description: err.message || "Error", variant: "destructive" });
                                                                } finally {
                                                                    setUpdatingVisibility(false);
                                                                }
                                                            }}
                                                            className={`
                                                              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                                                              ${isActive ? "bg-primary" : "bg-gray-200 dark:bg-gray-700"}
                                                              ${updatingVisibility ? "opacity-70 cursor-not-allowed" : ""}
                                                            `}
                                                            aria-pressed={isActive}
                                                            aria-label="Toggle Visibility"
                                                        >
                                                            <span className="sr-only">Visibility</span>
                                                            <span
                                                                className={`
                                                                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform dark:bg-gray-200
                                                                    ${isActive ? "translate-x-5" : "translate-x-1"}
                                                                `}
                                                            />
                                                            {updatingVisibility && (
                                                                <span className="absolute right-1 top-1 animate-spin text-xs text-primary">⏳</span>
                                                            )}
                                                        </button>
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <h3 className="text-lg font-medium text-gray-800 dark:text-white">
                                                                Automatic Confirmation
                                                            </h3>
                                                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                                                Automatically confirm bookings without manual approval.
                                                            </p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            disabled={updatingAutoAccept}
                                                            onClick={async () => {
                                                                try {
                                                                    setUpdatingAutoAccept(true);
                                                                    const updated = await updateRestaurantSettings(selectedRestaurantId!, {
                                                                        automaticConfirmation: !isAutoAccept,
                                                                    });
                                                                    setIsAutoAccept(!!updated.is_auto_accept);
                                                                    toast({ title: "Updated", description: "Auto-accept updated." });
                                                                } catch (err: any) {
                                                                    toast({ title: "Update failed", description: err.message || "Error", variant: "destructive" });
                                                                } finally {
                                                                    setUpdatingAutoAccept(false);
                                                                }
                                                            }}
                                                            className={`
                                                                relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                                                                ${isAutoAccept ? "bg-primary" : "bg-gray-200 dark:bg-gray-700"}
                                                                ${updatingVisibility ? "opacity-70 cursor-not-allowed" : ""}
                                                            `}
                                                            aria-pressed={isAutoAccept}
                                                            aria-label="Toggle Automatic Confirmation"
                                                        >
                                                            <span className="sr-only">Automatic Confirmation</span>
                                                            <span
                                                                className={`
                                                                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform dark:bg-gray-200
                                                                    ${isAutoAccept ? "translate-x-5" : "translate-x-1"}
                                                                `}
                                                            />
                                                            {updatingVisibility && (
                                                                <span className="absolute right-1 top-1 animate-spin text-xs text-primary">⏳</span>
                                                            )}
                                                        </button>
                                                    </div>

                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <h3 className="text-lg font-medium text-gray-800 dark:text-white">
                                                                Email Notifications
                                                            </h3>
                                                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                                                Receive email notifications for new bookings.
                                                            </p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            disabled={updatingEmailNotifications}
                                                            onClick={async () => {
                                                                try {
                                                                    setUpdatingEmailNotifications(true);
                                                                    const updated = await updateRestaurantSettings(selectedRestaurantId!, {
                                                                        emailNotifications: !isEmailEnabled,
                                                                    });
                                                                    setIsEmailEnabled(!!updated.is_email_enabled);
                                                                    toast({ title: "Updated", description: "Email notifications updated." });
                                                                } catch (err: any) {
                                                                    toast({ title: "Update failed", description: err.message || "Error", variant: "destructive" });
                                                                } finally {
                                                                    setUpdatingEmailNotifications(false);
                                                                }
                                                            }}
                                                            className={`
                                                              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                                                              ${isEmailEnabled ? "bg-primary" : "bg-gray-200 dark:bg-gray-700"}
                                                              ${updatingVisibility ? "opacity-70 cursor-not-allowed" : ""}
                                                            `}
                                                            aria-pressed={isEmailEnabled}
                                                            aria-label="Toggle Automatic Confirmation"
                                                        >
                                                            <span className="sr-only">Automatic Confirmation</span>
                                                            <span
                                                                className={`
                                                                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform dark:bg-gray-200
                                                                    ${isEmailEnabled ? "translate-x-5" : "translate-x-1"}
                                                                `}
                                                            />
                                                            {updatingVisibility && (
                                                                <span className="absolute right-1 top-1 animate-spin text-xs text-primary">⏳</span>
                                                            )}
                                                        </button>
                                                    </div>

                                                    <div className="pt-4">
                                                        <Button
                                                            variant="destructive"
                                                            className="w-full sm:w-auto"
                                                        >
                                                            Delete Restaurant
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </TabsContent>
                                </Tabs>
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default RestaurantDashboard;
